import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { childrenApi, ApiError } from '@/lib/children-api'
import type { Child, SubscriptionInfo } from '@/types/child'

export function ChildrenListPage() {
  const [children, setChildren] = useState<Child[]>([])
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadChildren()
  }, [])

  const loadChildren = async () => {
    try {
      setIsLoading(true)
      const response = await childrenApi.list()
      setChildren(response.children)
      setSubscription(response.subscription)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load children')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to remove ${name}?`)) return

    try {
      await childrenApi.delete(id)
      setChildren(children.filter((c) => c.id !== id))
      if (subscription) {
        setSubscription({
          ...subscription,
          used: subscription.used - 1,
          remaining: subscription.remaining === 'unlimited'
            ? 'unlimited'
            : (subscription.remaining as number) + 1,
        })
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete child')
    }
  }

  if (isLoading) {
    return <div className="p-4">Loading...</div>
  }

  const canAddMore = subscription && (
    subscription.remaining === 'unlimited' || subscription.remaining > 0
  )

  return (
    <div className="p-4">
      <div className="mb-4">
        <Link to="/settings" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Settings
        </Link>
      </div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Children</h1>
        {canAddMore ? (
          <Link to="/children/new" className="btn-primary">
            Add Child
          </Link>
        ) : (
          <Link to="/settings" className="btn-primary">
            Upgrade
          </Link>
        )}
      </div>

      {subscription && (
        <div className="mb-4 rounded bg-gray-100 p-3 text-sm">
          <span className="font-medium capitalize">{subscription.tier} Plan:</span>{' '}
          {subscription.used} / {subscription.limit === Infinity ? '∞' : subscription.limit} child profiles
          {!canAddMore && (
            <Link to="/settings" className="ml-2 text-primary-600 hover:underline">
              — Upgrade to add more
            </Link>
          )}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {children.length === 0 ? (
        <div className="rounded border border-dashed border-gray-300 p-8 text-center">
          <p className="text-gray-500">No children added yet</p>
          {canAddMore && (
            <Link to="/children/new" className="mt-2 inline-block text-primary-600 hover:underline">
              Add your first child
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {children.map((child) => (
            <div
              key={child.id}
              className={`flex items-center justify-between rounded border p-4 ${
                child.isLocked
                  ? 'border-amber-200 bg-amber-50'
                  : 'border-gray-200'
              }`}
            >
              <div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/children/${child.id}`}
                    className="font-medium hover:text-primary-600 ph-no-capture"
                  >
                    {child.firstName} {child.lastName}
                  </Link>
                  {child.isLocked && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Locked
                    </span>
                  )}
                </div>
                {child.parents.length > 0 && (
                  <p className="text-sm text-gray-500 ph-no-capture">
                    {child.parents.map((p) => p.name).join(', ')}
                  </p>
                )}
                {child.isLocked && (
                  <p className="mt-1 text-xs text-amber-600">
                    Read-only — <Link to="/settings" className="underline hover:text-amber-700">upgrade to unlock</Link>
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Link
                  to={`/children/${child.id}`}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  View
                </Link>
                <button
                  onClick={() => handleDelete(child.id, `${child.firstName} ${child.lastName}`)}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
